import React, {
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  Checkbox
} from '@mui/material';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import PetsRoundedIcon from '@mui/icons-material/PetsRounded';
import LoginRoundedIcon from '@mui/icons-material/LoginRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import WbSunnyRoundedIcon from '@mui/icons-material/WbSunnyRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import AssignmentRoundedIcon from '@mui/icons-material/AssignmentRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';

import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient.ts';

type AgendaDay = {
  date: string;
  daytime_dogs: number;
  overnight_dogs: number;
  daycare_blocked: boolean;
  overnight_blocked: boolean;
};

type AgendaDog = {
  id: string;
  name: string;
  breed: string | null;
  sex: string | null;
  active: boolean;
  capacity_exception: boolean;
};

type AgendaReservation = {
  id: string;
  family_id: string;
  booking_type: string;
  start_date: string;
  end_date: string;
  arrival_time: string | null;
  departure_time: string | null;
  status: string;
  option_expires_at: string | null;
  total_amount: number | null;
  counts_for_capacity: boolean;
  customer: {
    first_name: string | null;
    last_name: string | null;
    email: string | null;
    phone: string | null;
  } | null;
  dogs: AgendaDog[];
  shortening?: any;
};

type AgendaHold = {
  reservation_id: string;
  care_date: string;
  daytime: boolean;
  overnight: boolean;
  dog_names: string;
  amount_to_release: number;
};

type AgendaBlock = {
  id: string;
  start_date: string;
  end_date: string;
  reason: string | null;
  blocks_daycare: boolean;
  blocks_overnight: boolean;
};

type AgendaPayload = {
  version: number;
  generated_at: string;
  from_date: string;
  through_date: string;
  days: AgendaDay[];
  reservations: AgendaReservation[];
  shortening_holds: AgendaHold[];
  blocks: AgendaBlock[];
};

type DogListItem = {
  key: string;
  name: string;
  detail: string;
  special?: boolean;
  status?: string | null;
};

type BookingDog = {
  id: string;
  name: string;
  breed: string | null;
  direct_booking_allowed: boolean | null;
  capacity_exception: boolean | null;
  active: boolean | null;
};

type BookingFamily = {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  moneybird_contact_id: string | null;
  dogs: BookingDog[];
};

type BookingPreview = {
  allowed: boolean;
  reason: string;
  total_amount: number;
  minimum_arrival_time: string | null;
  arrival_advice: string | null;
  manual_dogs: string[];
  price_lines: any[];
  capacity_override_requested?: boolean;
  capacity_override_used?: boolean;
};

type DemandDay = {
  demand_date: string;
  requested_checks: number;
  unique_sessions: number;
  unavailable_checks: number;
  submitted_requests: number;
};

type DemandMonth = {
  month: string;
  label: string;
  requestedCareDays: number;
  unavailableCareDays: number;
  submittedApplications: number;
  submittedCareDays: number;
  measuredDays: number;
};

type DemandApplicationSummary = {
  total_applications: number;
  months: Array<{
    month_start: string;
    submitted_applications: number;
  }>;
};

type NewCustomerBooking = {
  reservation_id: string;
  family_id: string;
  customer_name: string;
  customer_email: string | null;
  booking_type: string;
  start_date: string;
  end_date: string;
  status: string;
  total_amount: number | null;
  dog_names: string;
};

const nlLong = new Intl.DateTimeFormat('nl-NL', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric'
});

const nlShort = new Intl.DateTimeFormat('nl-NL', {
  weekday: 'short',
  day: 'numeric',
  month: 'short'
});

function amsterdamIsoDate(date = new Date()) {
  const parts = new Intl.DateTimeFormat('nl-NL', {
    timeZone: 'Europe/Amsterdam',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).formatToParts(date);

  const year = parts.find(part => part.type === 'year')?.value ?? '';
  const month = parts.find(part => part.type === 'month')?.value ?? '';
  const day = parts.find(part => part.type === 'day')?.value ?? '';

  return `${year}-${month}-${day}`;
}

function parseDate(value: string) {
  return new Date(`${value}T12:00:00`);
}

function addDays(value: string, days: number) {
  const date = parseDate(value);
  date.setDate(date.getDate() + days);

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

function shortTime(value: string | null) {
  if (!value) return null;
  return value.slice(0, 5);
}

function customerName(r: AgendaReservation) {
  const value = `${r.customer?.first_name ?? ''} ${r.customer?.last_name ?? ''}`.trim();
  return value || r.customer?.email || 'Onbekende klant';
}

function statusLabel(status: string) {
  if (status === 'confirmed') return null;

  return ({
    option: 'optie',
    cancel_pending_payment: 'wacht op betaling'
  } as Record<string, string | null>)[status] ?? status;
}

function money(value: number) {
  return new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: 'EUR'
  }).format(Number(value));
}

function summarizePriceLines(lines: any[]) {
  const groups = new Map<
    string,
    {
      dog_name: string;
      description: string;
      base_rate: number;
      discount_percent: number;
      final_rate: number;
      count: number;
      total: number;
    }
  >();

  for (const raw of lines ?? []) {
    const dogName = String(raw?.dog_name ?? 'Hond');
    const description = String(raw?.description ?? '');
    const baseRate = Number(raw?.base_rate ?? 0);
    const discountPercent = Number(raw?.discount_percent ?? 0);
    const finalRate = Number(raw?.final_rate ?? 0);

    const key = [
      dogName,
      description,
      baseRate,
      discountPercent,
      finalRate
    ].join('|');

    const current = groups.get(key);

    if (current) {
      current.count += 1;
      current.total += finalRate;
    } else {
      groups.set(key, {
        dog_name: dogName,
        description,
        base_rate: baseRate,
        discount_percent: discountPercent,
        final_rate: finalRate,
        count: 1,
        total: finalRate
      });
    }
  }

  return Array.from(groups.values());
}

function bookingOwnerName(family: BookingFamily) {
  return [family.first_name, family.last_name]
    .filter(Boolean)
    .join(' ')
    .trim() || family.email || 'Naam ontbreekt';
}

function demandMonthLabel(monthStart: string) {
  return new Intl.DateTimeFormat('nl-NL', {
    month: 'long',
    year: 'numeric'
  }).format(parseDate(monthStart));
}

function csvValue(value: string | number) {
  const textValue = String(value ?? '');
  return `"${textValue.replace(/"/g, '""')}"`;
}

function occupancyLabel(day: AgendaDay | null) {
  if (!day) return 'Geen gegevens';

  if (day.daycare_blocked && day.overnight_blocked) {
    return 'Geblokkeerd';
  }

  if (day.daycare_blocked || day.overnight_blocked) {
    return 'Deels geblokkeerd';
  }

  const highest = Math.max(
    Number(day.daytime_dogs || 0),
    Number(day.overnight_dogs || 0)
  );

  if (highest >= 4) return `${highest} honden · boven limiet`;
  if (highest === 3) return '3 honden · derde plek';
  if (highest === 2) return '2 honden · vol';
  if (highest === 1) return '1 hond · ruimte';
  return 'Vrij';
}

function occupancyChipSx(day: AgendaDay | null) {
  if (!day) {
    return {
      bgcolor: '#eeeeee',
      color: '#555555',
      fontWeight: 700
    };
  }

  if (day.daycare_blocked || day.overnight_blocked) {
    return {
      bgcolor: '#f5c2c7',
      color: '#7a1f2b',
      fontWeight: 700
    };
  }

  const highest = Math.max(
    Number(day.daytime_dogs || 0),
    Number(day.overnight_dogs || 0)
  );

  if (highest >= 4) {
    return {
      bgcolor: '#f5c2c7',
      color: '#7a1f2b',
      fontWeight: 700
    };
  }

  if (highest === 3) {
    return {
      bgcolor: '#8E6BBE',
      color: '#ffffff',
      fontWeight: 700
    };
  }

  if (highest === 2) {
    return {
      bgcolor: '#F8A5B6',
      color: '#3B2B31',
      fontWeight: 700
    };
  }

  return {
    bgcolor: '#D7E4C0',
    color: '#2E3827',
    fontWeight: 700
  };
}

function reservationDogs(
  reservation: AgendaReservation,
  detail: string
): DogListItem[] {
  return (reservation.dogs ?? [])
    .filter(dog => dog.active !== false)
    .map(dog => ({
      key: `${reservation.id}-${dog.id}`,
      name: dog.name,
      detail: `${detail} · ${customerName(reservation)}`,
      special: dog.capacity_exception,
      status: statusLabel(reservation.status)
    }));
}

function DogList({
  items,
  empty = 'Geen honden'
}: {
  items: DogListItem[];
  empty?: string;
}) {
  if (!items.length) {
    return (
      <Typography color="text.secondary">
        {empty}
      </Typography>
    );
  }

  return (
    <List disablePadding>
      {items.map(item => (
        <ListItem
          key={item.key}
          disableGutters
          sx={{ py: 0.75 }}
        >
          <ListItemAvatar>
            <Avatar
              sx={{
                bgcolor: 'secondary.light',
                color: 'text.primary'
              }}
            >
              <PetsRoundedIcon />
            </Avatar>
          </ListItemAvatar>

          <ListItemText
            primary={
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
              >
                <b>{item.name}</b>

                {item.special && (
                  <Chip
                    size="small"
                    label="uitzonderingshond"
                    sx={{
                      bgcolor: '#E8DDF4',
                      color: '#5E4978',
                      fontWeight: 700,
                      border: '1px solid #D8C7E8'
                    }}
                  />
                )}

                {item.status && (
                  <Chip
                    size="small"
                    label={item.status}
                    variant="outlined"
                  />
                )}
              </Stack>
            }
            secondary={item.detail}
          />
        </ListItem>
      ))}
    </List>
  );
}

export const AdminDashboardPage = () => {
  const navigate = useNavigate();

  const todayIso = useMemo(() => amsterdamIsoDate(), []);
  const throughIso = useMemo(() => addDays(todayIso, 6), [todayIso]);

  const [tab, setTab] = useState(0);
  const [loggingOut, setLoggingOut] = useState(false);
  const [agenda, setAgenda] = useState<AgendaPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newCustomerBookings, setNewCustomerBookings] = useState<NewCustomerBooking[]>([]);
  const [newCustomerBookingsLoading, setNewCustomerBookingsLoading] = useState(false);
  const [newCustomerBookingsError, setNewCustomerBookingsError] = useState('');
  const [markingSeenId, setMarkingSeenId] = useState<string | null>(null);

  const [bookingOpen, setBookingOpen] = useState(false);
  const [bookingFamilies, setBookingFamilies] = useState<BookingFamily[]>([]);
  const [bookingFamiliesLoading, setBookingFamiliesLoading] = useState(false);
  const [bookingFamilyId, setBookingFamilyId] = useState('');
  const [bookingDogIds, setBookingDogIds] = useState<string[]>([]);
  const [bookingType, setBookingType] = useState<'dagopvang' | 'logeren'>('logeren');
  const [bookingStartDate, setBookingStartDate] = useState(todayIso);
  const [bookingEndDate, setBookingEndDate] = useState(addDays(todayIso, 1));
  const [bookingArrivalTime, setBookingArrivalTime] = useState('14:00');
  const [bookingDepartureTime, setBookingDepartureTime] = useState('10:00');
  const [bookingPreview, setBookingPreview] = useState<BookingPreview | null>(null);
  const [bookingBusy, setBookingBusy] = useState(false);
  const [bookingError, setBookingError] = useState('');
  const [bookingSuccess, setBookingSuccess] = useState('');
  const [bookingConfirmed, setBookingConfirmed] = useState(false);
  const [bookingCapacityOverride, setBookingCapacityOverride] = useState(false);
  const [bookingCapacityConfirmed, setBookingCapacityConfirmed] = useState(false);
  const [bookingQuoteRequired, setBookingQuoteRequired] = useState(true);

  const [blockOpen, setBlockOpen] = useState(false);
  const [blockStartDate, setBlockStartDate] = useState(todayIso);
  const [blockEndDate, setBlockEndDate] = useState(todayIso);
  const [blockReason, setBlockReason] = useState('');
  const [blockDaycare, setBlockDaycare] = useState(true);
  const [blockOvernight, setBlockOvernight] = useState(true);
  const [blockRows, setBlockRows] = useState<AgendaBlock[]>([]);
  const [blockLoading, setBlockLoading] = useState(false);
  const [blockBusy, setBlockBusy] = useState(false);
  const [blockError, setBlockError] = useState('');
  const [blockSuccess, setBlockSuccess] = useState('');

  const [demandFrom, setDemandFrom] = useState(todayIso);
  const [demandTo, setDemandTo] = useState(addDays(todayIso, 365));
  const [demandRows, setDemandRows] = useState<DemandDay[]>([]);
  const [demandApplicationSummary, setDemandApplicationSummary] =
    useState<DemandApplicationSummary>({
      total_applications: 0,
      months: []
    });
  const [demandLoading, setDemandLoading] = useState(false);
  const [demandError, setDemandError] = useState('');

  const dateTitle = useMemo(
    () => nlLong.format(parseDate(todayIso)),
    [todayIso]
  );

  const nav = [
    ['Vandaag', <WbSunnyRoundedIcon />],
    ['Agenda', <CalendarMonthRoundedIcon />],
    ['Honden', <PetsRoundedIcon />],
    ['Klanten', <GroupsRoundedIcon />],
    ['Aanvragen', <AssignmentRoundedIcon />],
    ['Aanvraagstatistiek', <BarChartRoundedIcon />]
  ];

  const statsTabIndex = 5;

  const loadToday = useCallback(async () => {
    if (!supabase) {
      setError('Supabase is niet beschikbaar.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');

    const { data, error: rpcError } = await supabase.rpc(
      'admin_get_agenda',
      {
        p_start_date: todayIso,
        p_end_date: throughIso
      }
    );

    if (rpcError) {
      setAgenda(null);
      setError(`Gegevens voor vandaag konden niet worden geladen: ${rpcError.message}`);
    } else {
      setAgenda((data ?? null) as AgendaPayload | null);
    }

    setLoading(false);
  }, [todayIso, throughIso]);

  useEffect(() => {
    loadToday();
  }, [loadToday]);

  const loadNewCustomerBookings = useCallback(async () => {
    if (!supabase) return;

    setNewCustomerBookingsLoading(true);
    setNewCustomerBookingsError('');

    const { data, error: rpcError } = await supabase.rpc(
      'admin_list_new_customer_bookings_v1'
    );

    if (rpcError) {
      setNewCustomerBookings([]);
      setNewCustomerBookingsError(
        `Nieuwe Mijn Pippaya-boekingen konden niet worden geladen: ${rpcError.message}`
      );
    } else {
      setNewCustomerBookings((data ?? []) as NewCustomerBooking[]);
    }

    setNewCustomerBookingsLoading(false);
  }, []);

  useEffect(() => {
    void loadNewCustomerBookings();
  }, [loadNewCustomerBookings]);

  async function markCustomerBookingSeen(reservationId: string) {
    if (!supabase || markingSeenId) return;

    setMarkingSeenId(reservationId);
    setNewCustomerBookingsError('');

    const { error: rpcError } = await supabase.rpc(
      'admin_mark_customer_booking_seen_v1',
      { p_reservation_id: reservationId }
    );

    if (rpcError) {
      setNewCustomerBookingsError(
        `De boeking kon niet als gezien worden gemarkeerd: ${rpcError.message}`
      );
    } else {
      setNewCustomerBookings(current =>
        current.filter(item => item.reservation_id !== reservationId)
      );
    }

    setMarkingSeenId(null);
  }

  const loadDemand = useCallback(async () => {
    if (!supabase) {
      setDemandError('Supabase is niet beschikbaar.');
      return;
    }

    if (!demandFrom || !demandTo || demandTo < demandFrom) {
      setDemandError('Kies een geldige periode.');
      return;
    }

    setDemandLoading(true);
    setDemandError('');

    const [dailyResult, applicationResult] = await Promise.all([
      supabase.rpc(
        'admin_intake_demand_by_date_v1',
        {
          p_from_date: demandFrom,
          p_to_date: demandTo
        }
      ),
      supabase.rpc(
        'admin_intake_application_summary_v1',
        {
          p_from_date: demandFrom,
          p_to_date: demandTo
        }
      )
    ]);

    if (dailyResult.error || applicationResult.error) {
      setDemandRows([]);
      setDemandApplicationSummary({
        total_applications: 0,
        months: []
      });
      setDemandError(
        `Aanvraagstatistiek kon niet worden geladen: ${
          dailyResult.error?.message ||
          applicationResult.error?.message ||
          'onbekende fout'
        }`
      );
    } else {
      setDemandRows(
        ((dailyResult.data ?? []) as DemandDay[]).map(row => ({
          ...row,
          requested_checks: Number(row.requested_checks || 0),
          unique_sessions: Number(row.unique_sessions || 0),
          unavailable_checks: Number(row.unavailable_checks || 0),
          submitted_requests: Number(row.submitted_requests || 0)
        }))
      );

      const summary =
        (applicationResult.data ?? {}) as Partial<DemandApplicationSummary>;

      setDemandApplicationSummary({
        total_applications: Number(summary.total_applications || 0),
        months: Array.isArray(summary.months)
          ? summary.months.map(row => ({
              month_start: String(row.month_start),
              submitted_applications: Number(
                row.submitted_applications || 0
              )
            }))
          : []
      });
    }

    setDemandLoading(false);
  }, [demandFrom, demandTo]);

  const demandMonths = useMemo(() => {
    if (!demandFrom || !demandTo || demandTo < demandFrom) {
      return [] as DemandMonth[];
    }

    const map = new Map<string, DemandMonth>();
    const cursor = new Date(`${demandFrom.slice(0, 7)}-01T12:00:00`);
    const last = new Date(`${demandTo.slice(0, 7)}-01T12:00:00`);

    while (cursor <= last) {
      const year = cursor.getFullYear();
      const month = String(cursor.getMonth() + 1).padStart(2, '0');
      const key = `${year}-${month}`;
      const monthStart = `${key}-01`;

      map.set(key, {
        month: key,
        label: demandMonthLabel(monthStart),
        requestedCareDays: 0,
        unavailableCareDays: 0,
        submittedApplications: 0,
        submittedCareDays: 0,
        measuredDays: 0
      });

      cursor.setMonth(cursor.getMonth() + 1);
    }

    demandRows.forEach(row => {
      const key = row.demand_date.slice(0, 7);
      const current = map.get(key);

      if (!current) return;

      current.requestedCareDays += Number(row.requested_checks || 0);
      current.unavailableCareDays += Number(row.unavailable_checks || 0);
      current.submittedCareDays += Number(row.submitted_requests || 0);

      if (Number(row.requested_checks || 0) > 0) {
        current.measuredDays += 1;
      }
    });

    demandApplicationSummary.months.forEach(row => {
      const key = row.month_start.slice(0, 7);
      const current = map.get(key);

      if (!current) return;

      current.submittedApplications = Number(
        row.submitted_applications || 0
      );
    });

    return Array.from(map.values());
  }, [
    demandFrom,
    demandTo,
    demandRows,
    demandApplicationSummary
  ]);

  const demandTotals = useMemo(() => {
    const totals = demandRows.reduce(
      (total, row) => {
        total.requested += Number(row.requested_checks || 0);
        total.unavailable += Number(row.unavailable_checks || 0);
        total.submitted += Number(row.submitted_requests || 0);

        if (Number(row.requested_checks || 0) > 0) {
          total.measuredDays += 1;
        }

        return total;
      },
      {
        requested: 0,
        unavailable: 0,
        submitted: 0,
        measuredDays: 0
      }
    );

    return {
      ...totals,
      applications: Number(
        demandApplicationSummary.total_applications || 0
      )
    };
  }, [demandRows, demandApplicationSummary]);

  const busiestDemandDays = useMemo(
    () =>
      demandRows
        .filter(row => Number(row.requested_checks || 0) > 0)
        .slice()
        .sort((a, b) => {
          const byDemand =
            Number(b.requested_checks || 0) - Number(a.requested_checks || 0);

          if (byDemand !== 0) return byDemand;

          return a.demand_date.localeCompare(b.demand_date);
        })
        .slice(0, 10),
    [demandRows]
  );

  function downloadDemandCsv() {
    const header = [
      'opvangdatum',
      'gevraagde_opvangmomenten',
      'unieke_formuliersessies',
      'niet_beschikbaar',
      'ingediende_aanvragen_voor_deze_opvangdatum'
    ];

    const lines = [
      header.map(csvValue).join(';'),
      ...demandRows.map(row =>
        [
          row.demand_date,
          row.requested_checks,
          row.unique_sessions,
          row.unavailable_checks,
          row.submitted_requests
        ]
          .map(csvValue)
          .join(';')
      )
    ];

    const blob = new Blob([`\uFEFF${lines.join('\r\n')}`], {
      type: 'text/csv;charset=utf-8'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = `pippaya-aanvraagstatistiek-${demandFrom}-tot-${demandTo}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }

  const activeReservations = useMemo(
    () => (agenda?.reservations ?? []).filter(r => r.counts_for_capacity),
    [agenda]
  );

  const staying = useMemo(() => {
    return activeReservations
      .filter(
        r =>
          r.booking_type === 'logeren' &&
          r.start_date < todayIso &&
          r.end_date > todayIso
      )
      .flatMap(r => {
        const departure = shortTime(r.departure_time);
        const detail = departure
          ? `Logeert · vertrek ${nlLong.format(parseDate(r.end_date))} · ${departure} uur`
          : `Logeert · vertrek ${nlLong.format(parseDate(r.end_date))}`;

        return reservationDogs(r, detail);
      });
  }, [activeReservations, todayIso]);

  const arrivals = useMemo(() => {
    return activeReservations
      .filter(r => r.start_date === todayIso)
      .flatMap(r => {
        const arrival = shortTime(r.arrival_time);

        const detail =
          r.booking_type === 'dagopvang'
            ? arrival
              ? `Dagopvang · brengen ${arrival} uur`
              : 'Dagopvang · komt vandaag'
            : arrival
              ? `Logeren · brengen ${arrival} uur`
              : 'Logeren · komt vandaag';

        return reservationDogs(r, detail);
      });
  }, [activeReservations, todayIso]);

  const departures = useMemo(() => {
    return activeReservations
      .filter(r => r.end_date === todayIso)
      .flatMap(r => {
        const departure = shortTime(r.departure_time);

        const detail = departure
          ? `Ophalen ${departure} uur`
          : 'Gaat vandaag naar huis';

        return reservationDogs(r, detail);
      });
  }, [activeReservations, todayIso]);

  const todayDay = useMemo(
    () => (agenda?.days ?? []).find(day => day.date === todayIso) ?? null,
    [agenda, todayIso]
  );

  const todayBlocks = useMemo(
    () =>
      (agenda?.blocks ?? []).filter(
        block =>
          block.start_date <= todayIso &&
          block.end_date >= todayIso
      ),
    [agenda, todayIso]
  );

  const todayHolds = useMemo(
    () =>
      (agenda?.shortening_holds ?? []).filter(
        hold => hold.care_date === todayIso
      ),
    [agenda, todayIso]
  );

  const nextDays = useMemo(
    () => agenda?.days ?? [],
    [agenda]
  );

  function dogsForDate(date: string) {
    const names = activeReservations
      .filter(
        r =>
          r.start_date <= date &&
          r.end_date >= date
      )
      .flatMap(r => r.dogs ?? [])
      .filter(dog => dog.active !== false)
      .map(dog => dog.name);

    return Array.from(new Set(names)).sort((a, b) =>
      a.localeCompare(b, 'nl')
    );
  }

  const selectedBookingFamily = useMemo(
    () =>
      bookingFamilies.find(family => family.id === bookingFamilyId) ?? null,
    [bookingFamilies, bookingFamilyId]
  );

  const activeBookingDogs = useMemo(
    () =>
      (selectedBookingFamily?.dogs ?? [])
        .filter(dog => dog.active !== false)
        .sort((a, b) => a.name.localeCompare(b.name, 'nl')),
    [selectedBookingFamily]
  );

  function clearBookingPreview() {
    setBookingPreview(null);
    setBookingError('');
    setBookingSuccess('');
    setBookingConfirmed(false);
    setBookingCapacityOverride(false);
    setBookingCapacityConfirmed(false);
    setBookingQuoteRequired(true);
  }

  async function openBookingDialog() {
    setBookingOpen(true);
    setBookingError('');
    setBookingSuccess('');
    setBookingPreview(null);
    setBookingConfirmed(false);
    setBookingCapacityOverride(false);
    setBookingCapacityConfirmed(false);
    setBookingQuoteRequired(true);

    if (bookingFamilies.length || bookingFamiliesLoading || !supabase) {
      return;
    }

    setBookingFamiliesLoading(true);

    const { data, error: familiesError } = await supabase.rpc(
      'admin_list_customers_private_v1'
    );

    if (familiesError) {
      setBookingError(
        `Klanten konden niet worden geladen: ${familiesError.message}`
      );
      setBookingFamilies([]);
    } else {
      setBookingFamilies((data ?? []) as BookingFamily[]);
    }

    setBookingFamiliesLoading(false);
  }

  function changeBookingType(value: 'dagopvang' | 'logeren') {
    setBookingType(value);

    if (value === 'dagopvang') {
      setBookingEndDate(bookingStartDate);
      setBookingArrivalTime('08:00');
      setBookingDepartureTime('18:00');
    } else {
      setBookingEndDate(
        bookingEndDate > bookingStartDate
          ? bookingEndDate
          : addDays(bookingStartDate, 1)
      );
      setBookingArrivalTime('14:00');
      setBookingDepartureTime('10:00');
    }

    clearBookingPreview();
  }

  async function previewAdminBooking() {
    if (!supabase) return;

    setBookingError('');
    setBookingSuccess('');
    setBookingPreview(null);

    if (!bookingFamilyId) {
      setBookingError('Kies eerst een klant.');
      return;
    }

    if (!bookingDogIds.length) {
      setBookingError('Kies minimaal één hond.');
      return;
    }

    if (!bookingStartDate || !bookingEndDate) {
      setBookingError('Kies een geldige datum of periode.');
      return;
    }

    setBookingBusy(true);

    const { data, error: previewError } = await supabase.rpc(
      'admin_preview_reservation_v2',
      {
        p_family_id: bookingFamilyId,
        p_dog_ids: bookingDogIds,
        p_booking_type: bookingType,
        p_start_date: bookingStartDate,
        p_end_date:
          bookingType === 'dagopvang'
            ? bookingStartDate
            : bookingEndDate,
        p_arrival_time: bookingArrivalTime,
        p_departure_time: bookingDepartureTime,
        p_capacity_override: bookingCapacityOverride
      }
    );

    if (previewError) {
      setBookingError(previewError.message);
    } else {
      setBookingPreview(data as BookingPreview);
    }

    setBookingBusy(false);
  }

  async function createAdminBooking() {
    if (!supabase || !bookingPreview?.allowed) return;

    if (!bookingConfirmed) {
      setBookingError(
        'Bevestig eerst dat deze reservering al definitief is afgesproken of dat de offerte is goedgekeurd.'
      );
      return;
    }

    if (bookingPreview.capacity_override_used && !bookingCapacityConfirmed) {
      setBookingError(
        'Bevestig eerst dat je de capaciteitsoverschrijding bewust accepteert.'
      );
      return;
    }

    if (!selectedBookingFamily?.moneybird_contact_id) {
      setBookingError(
        'Deze klant heeft geen Moneybird-koppeling. Voeg deze reservering niet via dit scherm toe.'
      );
      return;
    }

    setBookingBusy(true);
    setBookingError('');
    setBookingSuccess('');

    const requestId = globalThis.crypto.randomUUID();

    const { data, error: createError } = await supabase.rpc(
      'admin_create_reservation_v3',
      {
        p_request_id: requestId,
        p_family_id: bookingFamilyId,
        p_dog_ids: bookingDogIds,
        p_booking_type: bookingType,
        p_start_date: bookingStartDate,
        p_end_date:
          bookingType === 'dagopvang'
            ? bookingStartDate
            : bookingEndDate,
        p_arrival_time: bookingArrivalTime,
        p_departure_time: bookingDepartureTime,
        p_make_option: false,
        p_expected_total: Number(bookingPreview.total_amount),
        p_capacity_override: bookingCapacityOverride,
        p_moneybird_quote_required: bookingQuoteRequired
      }
    );

    if (createError) {
      setBookingError(createError.message);
    } else {
      setBookingSuccess(
        bookingQuoteRequired
          ? `Reservering opgeslagen voor ${money(Number(data?.total_amount ?? bookingPreview.total_amount))}. De Moneybird-offerte wordt door de bestaande automatisering aangemaakt/verstuurd en moet door de klant online worden goedgekeurd voordat facturatie verdergaat.`
          : `Reservering opgeslagen voor ${money(Number(data?.total_amount ?? bookingPreview.total_amount))}. Geen nieuwe Moneybird-offerte vereist.`
      );
      setBookingPreview(null);
      setBookingConfirmed(false);
      setBookingCapacityOverride(false);
      setBookingCapacityConfirmed(false);
      setBookingQuoteRequired(true);
      await Promise.all([
        loadToday(),
        loadNewCustomerBookings()
      ]);
    }

    setBookingBusy(false);
  }

  const loadFutureBlocks = useCallback(async () => {
    if (!supabase) return;

    setBlockLoading(true);
    setBlockError('');

    const { data, error: blocksError } = await supabase
      .from('blocked_periods')
      .select('id,start_date,end_date,reason,blocks_daycare,blocks_overnight')
      .gte('end_date', todayIso)
      .order('start_date', { ascending: true });

    if (blocksError) {
      setBlockRows([]);
      setBlockError(`Blokkades konden niet worden geladen: ${blocksError.message}`);
    } else {
      setBlockRows((data ?? []) as AgendaBlock[]);
    }

    setBlockLoading(false);
  }, [todayIso]);

  async function openBlockDialog() {
    setBlockOpen(true);
    setBlockStartDate(todayIso);
    setBlockEndDate(todayIso);
    setBlockReason('');
    setBlockDaycare(true);
    setBlockOvernight(true);
    setBlockError('');
    setBlockSuccess('');
    await loadFutureBlocks();
  }

  async function saveBlock() {
    if (!supabase || blockBusy) return;

    setBlockError('');
    setBlockSuccess('');

    if (!blockStartDate || !blockEndDate) {
      setBlockError('Kies een geldige start- en einddatum.');
      return;
    }

    if (blockEndDate < blockStartDate) {
      setBlockError('De einddatum kan niet vóór de startdatum liggen.');
      return;
    }

    if (!blockDaycare && !blockOvernight) {
      setBlockError('Kies minimaal dagopvang of logeren om te blokkeren.');
      return;
    }

    setBlockBusy(true);

    const { error: insertError } = await supabase
      .from('blocked_periods')
      .insert({
        start_date: blockStartDate,
        end_date: blockEndDate,
        reason: blockReason.trim() || 'Handmatig geblokkeerd vanuit Pippaya-beheer',
        blocks_daycare: blockDaycare,
        blocks_overnight: blockOvernight
      });

    if (insertError) {
      setBlockError(`Blokkeren is niet gelukt: ${insertError.message}`);
    } else {
      setBlockSuccess(
        blockStartDate === blockEndDate
          ? 'De datum is direct geblokkeerd voor nieuwe boekingen.'
          : 'De periode is direct geblokkeerd voor nieuwe boekingen.'
      );
      setBlockReason('');
      await Promise.all([loadFutureBlocks(), loadToday()]);
    }

    setBlockBusy(false);
  }

  async function releaseBlock(block: AgendaBlock) {
    if (!supabase || blockBusy) return;

    const period =
      block.start_date === block.end_date
        ? nlLong.format(parseDate(block.start_date))
        : `${nlLong.format(parseDate(block.start_date))} t/m ${nlLong.format(parseDate(block.end_date))}`;

    if (!window.confirm(`Blokkade voor ${period} vrijgeven?`)) {
      return;
    }

    setBlockBusy(true);
    setBlockError('');
    setBlockSuccess('');

    const { error: deleteError } = await supabase
      .from('blocked_periods')
      .delete()
      .eq('id', block.id);

    if (deleteError) {
      setBlockError(`Vrijgeven is niet gelukt: ${deleteError.message}`);
    } else {
      setBlockSuccess('De blokkade is verwijderd. Deze periode is weer beschikbaar volgens de normale capaciteit.');
      await Promise.all([loadFutureBlocks(), loadToday()]);
    }

    setBlockBusy(false);
  }

  const handleTabChange = (
    _: unknown,
    value: number
  ) => {
    const label = nav[value][0];

    if (label === 'Aanvraagstatistiek') {
      setTab(value);
      void loadDemand();
      return;
    }

    if (label === 'Agenda') {
      navigate('/beheer/agenda');
      return;
    }

    if (label === 'Honden') {
      navigate('/beheer/honden');
      return;
    }

    if (label === 'Klanten') {
      navigate('/beheer/klanten');
      return;
    }

    if (label === 'Aanvragen') {
      navigate('/beheer/aanmeldingen');
      return;
    }

    setTab(value);
  };

  async function handleLogout() {
    if (!supabase) return;

    setLoggingOut(true);
    setError('');

    const { error: signOutError } =
      await supabase.auth.signOut();

    if (signOutError) {
      setError(
        'Uitloggen is niet gelukt. Probeer het opnieuw.'
      );
      setLoggingOut(false);
      return;
    }

    navigate('/inloggen', {
      replace: true
    });
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#fffafb',
        py: { xs: 2, md: 4 }
      }}
    >
      <Container maxWidth="xl">
        <Stack
          direction={{
            xs: 'column',
            md: 'row'
          }}
          justifyContent="space-between"
          gap={2}
          alignItems={{ md: 'center' }}
        >
          <Box>
            <Typography
              variant="overline"
              color="text.secondary"
            >
              Pippaya Hondenopvang · beheer
            </Typography>

            <Typography
              variant="h3"
              sx={{
                textTransform: 'capitalize'
              }}
            >
              {dateTitle}
            </Typography>

            <Typography color="text.secondary">
              {tab === statsTabIndex
                ? 'Inzicht in de vraag naar opvang per toekomstige opvangdatum.'
                : 'Live overzicht van wie er is, komt en naar huis gaat.'}
            </Typography>
          </Box>

          <Stack
            direction={{
              xs: 'column',
              sm: 'row'
            }}
            spacing={1}
          >
            <Button
              variant="outlined"
              size="large"
              startIcon={<RefreshRoundedIcon />}
              disabled={loading || demandLoading}
              onClick={
                tab === statsTabIndex
                  ? loadDemand
                  : async () => {
                      await Promise.all([
                        loadToday(),
                        loadNewCustomerBookings()
                      ]);
                    }
              }
            >
              Vernieuwen
            </Button>

            <Button
              variant="outlined"
              size="large"
              startIcon={<LogoutRoundedIcon />}
              disabled={loggingOut}
              onClick={handleLogout}
            >
              {loggingOut
                ? 'Uitloggen...'
                : 'Uitloggen'}
            </Button>

            <Button
              variant="outlined"
              size="large"
              startIcon={<CalendarMonthRoundedIcon />}
              onClick={openBlockDialog}
            >
              Datum/periode blokkeren
            </Button>

            <Button
              variant="contained"
              size="large"
              startIcon={<AddRoundedIcon />}
              onClick={openBookingDialog}
            >
              Reservering toevoegen
            </Button>
          </Stack>
        </Stack>

        {error && (
          <Alert
            severity="error"
            sx={{ mt: 2 }}
          >
            {error}
          </Alert>
        )}

        <Paper
          sx={{
            mt: 3,
            overflowX: 'auto'
          }}
        >
          <Tabs
            value={tab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            {nav.map(
              ([label, icon]: any) => (
                <Tab
                  key={label}
                  icon={icon}
                  iconPosition="start"
                  label={label}
                />
              )
            )}
          </Tabs>
        </Paper>

        {tab === 0 && (
          <>
            <Card sx={{ mt: 3, p: 2.5 }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ sm: 'center' }}
                gap={1}
              >
                <Box>
                  <Typography variant="h5">
                    Nieuwe boekingen via Mijn Pippaya
                  </Typography>
                  <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                    Nieuwe reserveringen die klanten zelf hebben gemaakt en die je nog niet hebt afgevinkt.
                  </Typography>
                </Box>

                <Chip
                  label={`${newCustomerBookings.length} nieuw`}
                  color={newCustomerBookings.length ? 'secondary' : 'default'}
                  sx={{ fontWeight: 800 }}
                />
              </Stack>

              {newCustomerBookingsError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {newCustomerBookingsError}
                </Alert>
              )}

              {newCustomerBookingsLoading ? (
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 2 }}>
                  <CircularProgress size={22} />
                  <Typography color="text.secondary">
                    Nieuwe boekingen laden...
                  </Typography>
                </Stack>
              ) : newCustomerBookings.length ? (
                <Stack spacing={1.25} sx={{ mt: 2 }}>
                  {newCustomerBookings.map(item => (
                    <Paper
                      key={item.reservation_id}
                      variant="outlined"
                      sx={{ p: 1.75, boxShadow: 'none' }}
                    >
                      <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        justifyContent="space-between"
                        alignItems={{ md: 'center' }}
                        gap={2}
                      >
                        <Box>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            flexWrap="wrap"
                            useFlexGap
                          >
                            <Typography fontWeight={800}>
                              {item.customer_name || item.customer_email || 'Onbekende klant'}
                            </Typography>

                            <Chip
                              size="small"
                              label="Mijn Pippaya"
                              sx={{
                                bgcolor: '#E8DDF4',
                                color: '#5E4978',
                                fontWeight: 700
                              }}
                            />

                            {item.status === 'option' && (
                              <Chip
                                size="small"
                                label="3-dagenoptie"
                                color="warning"
                                variant="outlined"
                              />
                            )}
                          </Stack>

                          <Typography sx={{ mt: 0.5 }}>
                            <b>{item.dog_names || 'Hond niet gevonden'}</b>
                            {' · '}
                            {item.booking_type === 'dagopvang' ? 'Dagopvang' : 'Logeren'}
                          </Typography>

                          <Typography variant="body2" color="text.secondary">
                            {item.booking_type === 'dagopvang'
                              ? nlLong.format(parseDate(item.start_date))
                              : `${nlLong.format(parseDate(item.start_date))} t/m ${nlLong.format(parseDate(item.end_date))}`}
                          </Typography>

                          {item.total_amount !== null && (
                            <Typography fontWeight={800} sx={{ mt: 0.5 }}>
                              {money(Number(item.total_amount))}
                            </Typography>
                          )}
                        </Box>

                        <Button
                          variant="contained"
                          disabled={markingSeenId === item.reservation_id}
                          onClick={() => markCustomerBookingSeen(item.reservation_id)}
                        >
                          {markingSeenId === item.reservation_id
                            ? 'Bezig...'
                            : 'Gezien'}
                        </Button>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              ) : (
                <Alert severity="success" sx={{ mt: 2 }}>
                  Geen nieuwe boekingen via Mijn Pippaya.
                </Alert>
              )}
            </Card>

            {loading && (
              <Stack
                direction="row"
                alignItems="center"
                spacing={1.5}
                sx={{ mt: 3 }}
              >
                <CircularProgress size={24} />
                <Typography color="text.secondary">
                  Gegevens van vandaag laden...
                </Typography>
              </Stack>
            )}

            {!loading && agenda && (
              <>
                <Grid
                  container
                  spacing={2.5}
                  sx={{ mt: 0.5 }}
                >
                  <Grid
                    item
                    xs={12}
                    md={4}
                  >
                    <Card
                      sx={{
                        p: 2.5,
                        height: '100%'
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                      >
                        <PetsRoundedIcon />

                        <Typography variant="h5">
                          Aanwezig / logeren
                        </Typography>
                      </Stack>

                      <Divider sx={{ my: 1.5 }} />

                      <DogList
                        items={staying}
                        empty="Geen honden die al logeren"
                      />
                    </Card>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    md={4}
                  >
                    <Card
                      sx={{
                        p: 2.5,
                        height: '100%'
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                      >
                        <LoginRoundedIcon />

                        <Typography variant="h5">
                          Komt vandaag
                        </Typography>
                      </Stack>

                      <Divider sx={{ my: 1.5 }} />

                      <DogList
                        items={arrivals}
                        empty="Vandaag komt niemand"
                      />
                    </Card>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    md={4}
                  >
                    <Card
                      sx={{
                        p: 2.5,
                        height: '100%'
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                      >
                        <LogoutRoundedIcon />

                        <Typography variant="h5">
                          Gaat vandaag naar huis
                        </Typography>
                      </Stack>

                      <Divider sx={{ my: 1.5 }} />

                      <DogList
                        items={departures}
                        empty="Vandaag gaat niemand naar huis"
                      />
                    </Card>
                  </Grid>
                </Grid>

                <Grid
                  container
                  spacing={2.5}
                  sx={{ mt: 0.25 }}
                >
                  <Grid
                    item
                    xs={12}
                    lg={8}
                  >
                    <Card sx={{ p: 2.5 }}>
                      <Stack
                        direction={{
                          xs: 'column',
                          sm: 'row'
                        }}
                        justifyContent="space-between"
                        alignItems={{
                          xs: 'flex-start',
                          sm: 'center'
                        }}
                        gap={1}
                        mb={2}
                      >
                        <Box>
                          <Typography variant="h5">
                            Komende 7 dagen
                          </Typography>

                          <Typography color="text.secondary">
                            Live bezetting vanaf vandaag.
                          </Typography>
                        </Box>

                        <Button
                          startIcon={<CalendarMonthRoundedIcon />}
                          onClick={() => navigate('/beheer/agenda')}
                        >
                          Volledige agenda
                        </Button>
                      </Stack>

                      <Grid
                        container
                        spacing={1.25}
                      >
                        {nextDays.map(day => {
                          const names = dogsForDate(day.date);

                          return (
                            <Grid
                              item
                              xs={12}
                              sm={6}
                              md
                              key={day.date}
                            >
                              <Paper
                                variant="outlined"
                                sx={{
                                  p: 1.5,
                                  minHeight: 170,
                                  boxShadow: 'none',
                                  borderColor:
                                    day.date === todayIso
                                      ? 'primary.main'
                                      : 'secondary.light',
                                  borderWidth:
                                    day.date === todayIso
                                      ? 2
                                      : 1
                                }}
                              >
                                <Typography
                                  fontWeight={800}
                                  sx={{
                                    textTransform:
                                      'capitalize'
                                  }}
                                >
                                  {nlShort.format(
                                    parseDate(day.date)
                                  )}
                                </Typography>

                                <Chip
                                  size="small"
                                  label={occupancyLabel(day)}
                                  sx={{
                                    my: 1,
                                    ...occupancyChipSx(day)
                                  }}
                                />

                                {names.map(name => (
                                  <Typography
                                    key={name}
                                    variant="body2"
                                  >
                                    🐾 {name}
                                  </Typography>
                                ))}

                                {!names.length && (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    Geen reserveringen
                                  </Typography>
                                )}
                              </Paper>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </Card>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    lg={4}
                  >
                    <Stack spacing={2}>
                      <Card sx={{ p: 2.5 }}>
                        <Typography variant="h5">
                          Bezetting vandaag
                        </Typography>

                        <Divider sx={{ my: 1.5 }} />

                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          gap={2}
                        >
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Overdag
                            </Typography>
                            <Typography variant="h4">
                              {todayDay?.daytime_dogs ?? 0}
                            </Typography>
                          </Box>

                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Overnachting
                            </Typography>
                            <Typography variant="h4">
                              {todayDay?.overnight_dogs ?? 0}
                            </Typography>
                          </Box>
                        </Stack>

                        <Chip
                          sx={{
                            mt: 1.5,
                            ...occupancyChipSx(todayDay)
                          }}
                          label={occupancyLabel(todayDay)}
                        />
                      </Card>

                      {todayBlocks.map(block => (
                        <Alert
                          key={block.id}
                          severity="warning"
                        >
                          <b>Blokkade vandaag</b>
                          <br />
                          {block.reason || 'Opvang geblokkeerd'}
                        </Alert>
                      ))}

                      {todayHolds.map(hold => (
                        <Alert
                          key={`${hold.reservation_id}-${hold.care_date}`}
                          severity="info"
                        >
                          <b>Plek nog gereserveerd</b>
                          <br />
                          {hold.dog_names}
                        </Alert>
                      ))}

                      {!todayBlocks.length &&
                        !todayHolds.length && (
                          <Alert severity="success">
                            <b>Vandaag</b>
                            <br />
                            Geen blokkades of vastgehouden plekken.
                          </Alert>
                        )}
                    </Stack>
                  </Grid>
                </Grid>
              </>
            )}
          </>
        )}

        {tab === statsTabIndex && (
          <>
            <Card sx={{ mt: 3, p: 2.5 }}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                justifyContent="space-between"
                alignItems={{ md: 'center' }}
                gap={2}
              >
                <Box>
                  <Typography variant="h4">
                    Aanvraagstatistiek
                  </Typography>
                  <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                    Dit overzicht telt vraag op de opvangdatum zelf. Ook een
                    beschikbaarheidscontrole die eindigt op ‘vol’ blijft als
                    vraagsignaal meetellen.
                  </Typography>
                </Box>

                <Button
                  variant="outlined"
                  startIcon={<DownloadRoundedIcon />}
                  disabled={demandLoading || !demandRows.length}
                  onClick={downloadDemandCsv}
                >
                  CSV downloaden
                </Button>
              </Stack>

              <Alert severity="info" sx={{ mt: 2 }}>
                De vraagregistratie is gestart op 2 oktober 2026. Een maand met
                weinig of nul metingen is daarom niet automatisch een rustige
                maand; dit overzicht wordt betrouwbaarder naarmate er langer
                gegevens worden verzameld.
              </Alert>

              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Vanaf opvangdatum"
                    InputLabelProps={{ shrink: true }}
                    value={demandFrom}
                    onChange={event => setDemandFrom(event.target.value)}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Tot en met opvangdatum"
                    InputLabelProps={{ shrink: true }}
                    value={demandTo}
                    onChange={event => setDemandTo(event.target.value)}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Button
                    fullWidth
                    variant="contained"
                    sx={{ height: '100%', minHeight: 56 }}
                    disabled={demandLoading}
                    onClick={loadDemand}
                  >
                    {demandLoading ? 'Laden...' : 'Periode laden'}
                  </Button>
                </Grid>
              </Grid>
            </Card>

            {demandError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {demandError}
              </Alert>
            )}

            {demandLoading && (
              <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
                sx={{ mt: 3 }}
              >
                <CircularProgress size={24} />
                <Typography color="text.secondary">
                  Aanvraagstatistiek laden...
                </Typography>
              </Stack>
            )}

            {!demandLoading && !demandError && (
              <>
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                  <Grid item xs={12} sm={6} lg={3}>
                    <Card sx={{ p: 2.25, height: '100%' }}>
                      <Typography variant="caption" color="text.secondary">
                        Gevraagde opvangdagen / nachten
                      </Typography>
                      <Typography variant="h3" sx={{ mt: 0.5 }}>
                        {demandTotals.requested}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Totaal aantal opvangdagen/nachten waarvoor belangstelling is gemeten.
                      </Typography>
                    </Card>
                  </Grid>

                  <Grid item xs={12} sm={6} lg={3}>
                    <Card sx={{ p: 2.25, height: '100%' }}>
                      <Typography variant="caption" color="text.secondary">
                        Daarvan niet beschikbaar
                      </Typography>
                      <Typography variant="h3" sx={{ mt: 0.5 }}>
                        {demandTotals.unavailable}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Gevraagde opvangdagen/nachten die niet binnen de actuele planning pasten.
                      </Typography>
                    </Card>
                  </Grid>

                  <Grid item xs={12} sm={6} lg={3}>
                    <Card sx={{ p: 2.25, height: '100%' }}>
                      <Typography variant="caption" color="text.secondary">
                        Aantal ingediende aanvragen
                      </Typography>
                      <Typography variant="h3" sx={{ mt: 0.5 }}>
                        {demandTotals.applications}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Verschillende opvangaanvragen die werkelijk zijn verstuurd.
                      </Typography>
                    </Card>
                  </Grid>

                  <Grid item xs={12} sm={6} lg={3}>
                    <Card sx={{ p: 2.25, height: '100%' }}>
                      <Typography variant="caption" color="text.secondary">
                        Opvangdagen / nachten uit aanvragen
                      </Typography>
                      <Typography variant="h3" sx={{ mt: 0.5 }}>
                        {demandTotals.submitted}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Totaal aantal opvangdagen/nachten binnen de daadwerkelijk ingediende aanvragen.
                      </Typography>
                    </Card>
                  </Grid>
                </Grid>

                <Grid container spacing={2.5} sx={{ mt: 0.25 }}>
                  <Grid item xs={12} lg={8}>
                    <Card sx={{ p: 2.5 }}>
                      <Typography variant="h5">
                        Opvangvraag per maand
                      </Typography>
                      <Typography color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
                        Handig om na verloop van tijd rustige en drukke
                        opvangperiodes te herkennen.
                      </Typography>

                      <Stack spacing={1}>
                        {demandMonths.map(month => (
                          <Paper
                            key={month.month}
                            variant="outlined"
                            sx={{
                              p: 1.5,
                              boxShadow: 'none'
                            }}
                          >
                            <Grid container spacing={1.5} alignItems="center">
                              <Grid item xs={12} sm={4}>
                                <Typography
                                  fontWeight={800}
                                  sx={{ textTransform: 'capitalize' }}
                                >
                                  {month.label}
                                </Typography>
                              </Grid>

                              <Grid item xs={6} sm={2}>
                                <Typography variant="caption" color="text.secondary">
                                  Gevraagde dagen/nachten
                                </Typography>
                                <Typography fontWeight={800}>
                                  {month.requestedCareDays}
                                </Typography>
                              </Grid>

                              <Grid item xs={6} sm={2}>
                                <Typography variant="caption" color="text.secondary">
                                  Niet beschikbaar
                                </Typography>
                                <Typography fontWeight={800}>
                                  {month.unavailableCareDays}
                                </Typography>
                              </Grid>

                              <Grid item xs={6} sm={2}>
                                <Typography variant="caption" color="text.secondary">
                                  Aantal aanvragen
                                </Typography>
                                <Typography fontWeight={800}>
                                  {month.submittedApplications}
                                </Typography>
                              </Grid>

                              <Grid item xs={6} sm={2}>
                                <Typography variant="caption" color="text.secondary">
                                  Dagen/nachten uit aanvragen
                                </Typography>
                                <Typography fontWeight={800}>
                                  {month.submittedCareDays}
                                </Typography>
                              </Grid>
                            </Grid>
                          </Paper>
                        ))}
                      </Stack>
                    </Card>
                  </Grid>

                  <Grid item xs={12} lg={4}>
                    <Card sx={{ p: 2.5 }}>
                      <Typography variant="h5">
                        Dagen met meeste gemeten vraag
                      </Typography>
                      <Typography color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
                        Maximaal de tien opvangdatums met de meeste
                        beschikbaarheidscontroles in de gekozen periode.
                      </Typography>

                      {busiestDemandDays.length ? (
                        <Stack spacing={1}>
                          {busiestDemandDays.map(row => (
                            <Paper
                              key={row.demand_date}
                              variant="outlined"
                              sx={{ p: 1.25, boxShadow: 'none' }}
                            >
                              <Stack
                                direction="row"
                                justifyContent="space-between"
                                gap={2}
                              >
                                <Box>
                                  <Typography fontWeight={800}>
                                    {nlShort.format(parseDate(row.demand_date))}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {row.unavailable_checks} niet beschikbaar ·{' '}
                                    {row.submitted_requests} aanvraag
                                    {row.submitted_requests === 1 ? '' : 'en'}
                                  </Typography>
                                </Box>

                                <Chip
                                  label={`${row.requested_checks}× gevraagd`}
                                  sx={{
                                    bgcolor: '#D7E4C0',
                                    color: '#2E3827',
                                    fontWeight: 800
                                  }}
                                />
                              </Stack>
                            </Paper>
                          ))}
                        </Stack>
                      ) : (
                        <Typography color="text.secondary">
                          Nog geen vraag gemeten in deze periode.
                        </Typography>
                      )}
                    </Card>
                  </Grid>
                </Grid>
              </>
            )}
          </>
        )}

        {tab !== 0 && tab !== statsTabIndex && (
          <Card
            sx={{
              mt: 3,
              p: 4
            }}
          >
            <Typography variant="h4">
              {nav[tab][0]}
            </Typography>

            <Typography
              color="text.secondary"
              sx={{ mt: 1 }}
            >
              Dit onderdeel werken we apart verder uit.
            </Typography>
          </Card>
        )}

        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          sx={{ mt: 3 }}
        >
          Live beheerdata · gekoppeld aan Supabase.
        </Typography>
      </Container>

      <Dialog
        open={bookingOpen}
        onClose={() => {
          if (!bookingBusy) {
            setBookingOpen(false);
          }
        }}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Reservering toevoegen</DialogTitle>

        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Gebruik dit scherm voor een opvangafspraak die definitief met de klant
            is afgesproken. Je kiest hieronder apart of de betaler nog een
            Moneybird-offerte moet ontvangen en online goedkeuren.
          </Alert>

          {bookingError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {bookingError}
            </Alert>
          )}

          {bookingSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {bookingSuccess}
            </Alert>
          )}

          {bookingFamiliesLoading ? (
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 3 }}>
              <CircularProgress size={24} />
              <Typography>Klanten laden...</Typography>
            </Stack>
          ) : (
            <Stack spacing={2}>
              <TextField
                select
                fullWidth
                label="Klant"
                value={bookingFamilyId}
                onChange={event => {
                  setBookingFamilyId(event.target.value);
                  setBookingDogIds([]);
                  clearBookingPreview();
                }}
              >
                {bookingFamilies
                  .slice()
                  .sort((a, b) =>
                    bookingOwnerName(a).localeCompare(bookingOwnerName(b), 'nl')
                  )
                  .map(family => (
                    <MenuItem key={family.id} value={family.id}>
                      {bookingOwnerName(family)}
                      {family.email ? ` · ${family.email}` : ''}
                    </MenuItem>
                  ))}
              </TextField>

              {selectedBookingFamily && (
                <>
                  {!selectedBookingFamily.moneybird_contact_id && (
                    <Alert severity="warning">
                      Deze klant heeft geen Moneybird-koppeling. Dit
                      reserveringsscherm is bedoeld voor normale boekingen die
                      via Moneybird worden afgehandeld.
                    </Alert>
                  )}

                  <Box>
                    <Typography fontWeight={800} sx={{ mb: 1 }}>
                      Hond(en)
                    </Typography>

                    {activeBookingDogs.length ? (
                      <Stack spacing={0.25}>
                        {activeBookingDogs.map(dog => (
                          <FormControlLabel
                            key={dog.id}
                            control={
                              <Checkbox
                                checked={bookingDogIds.includes(dog.id)}
                                onChange={event => {
                                  setBookingDogIds(current =>
                                    event.target.checked
                                      ? [...current, dog.id]
                                      : current.filter(id => id !== dog.id)
                                  );
                                  clearBookingPreview();
                                }}
                              />
                            }
                            label={
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                flexWrap="wrap"
                                useFlexGap
                              >
                                <span>{dog.name}</span>

                                {dog.capacity_exception && (
                                  <Chip
                                    size="small"
                                    label="uitzonderingshond"
                                    sx={{
                                      bgcolor: '#E8DDF4',
                                      color: '#5E4978',
                                      fontWeight: 700,
                                      border: '1px solid #D8C7E8'
                                    }}
                                  />
                                )}

                                {dog.direct_booking_allowed === false && (
                                  <Chip
                                    size="small"
                                    label="handmatige beoordeling"
                                    variant="outlined"
                                  />
                                )}
                              </Stack>
                            }
                          />
                        ))}
                      </Stack>
                    ) : (
                      <Typography color="text.secondary">
                        Deze klant heeft geen actieve honden.
                      </Typography>
                    )}
                  </Box>
                </>
              )}

              <TextField
                select
                label="Soort opvang"
                value={bookingType}
                onChange={event =>
                  changeBookingType(
                    event.target.value as 'dagopvang' | 'logeren'
                  )
                }
              >
                <MenuItem value="dagopvang">Dagopvang</MenuItem>
                <MenuItem value="logeren">Logeren</MenuItem>
              </TextField>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="date"
                    label={
                      bookingType === 'dagopvang'
                        ? 'Datum'
                        : 'Aankomstdatum'
                    }
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: todayIso }}
                    value={bookingStartDate}
                    onChange={event => {
                      const value = event.target.value;
                      setBookingStartDate(value);

                      if (bookingType === 'dagopvang') {
                        setBookingEndDate(value);
                      } else if (
                        !bookingEndDate ||
                        bookingEndDate <= value
                      ) {
                        setBookingEndDate(addDays(value, 1));
                      }

                      clearBookingPreview();
                    }}
                  />
                </Grid>

                {bookingType === 'logeren' && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="date"
                      label="Vertrekdatum"
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ min: addDays(bookingStartDate, 1) }}
                      value={bookingEndDate}
                      onChange={event => {
                        setBookingEndDate(event.target.value);
                        clearBookingPreview();
                      }}
                    />
                  </Grid>
                )}

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="time"
                    label="Brengtijd"
                    InputLabelProps={{ shrink: true }}
                    value={bookingArrivalTime}
                    onChange={event => {
                      setBookingArrivalTime(event.target.value);
                      clearBookingPreview();
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="time"
                    label="Ophaaltijd"
                    InputLabelProps={{ shrink: true }}
                    value={bookingDepartureTime}
                    onChange={event => {
                      setBookingDepartureTime(event.target.value);
                      clearBookingPreview();
                    }}
                  />
                </Grid>
              </Grid>

              <Alert severity="info">
                Normaal blijft de beheerlimiet maximaal 3 honden. Alleen als jij
                bewust wilt overschrijden, zet je onderstaande beheeroverschrijving aan.
                Blokkades en de reuenregel blijven altijd gelden.
              </Alert>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={bookingCapacityOverride}
                    disabled={bookingBusy}
                    onChange={event => {
                      setBookingCapacityOverride(event.target.checked);
                      setBookingPreview(null);
                      setBookingConfirmed(false);
                      setBookingCapacityConfirmed(false);
                      setBookingError('');
                      setBookingSuccess('');
                    }}
                  />
                }
                label="Capaciteitslimiet van 3 honden voor deze reservering bewust kunnen overschrijden"
              />

              <Button
                variant="outlined"
                disabled={
                  bookingBusy ||
                  !bookingFamilyId ||
                  !bookingDogIds.length
                }
                onClick={previewAdminBooking}
              >
                {bookingBusy
                  ? 'Controleren...'
                  : 'Beschikbaarheid en prijs controleren'}
              </Button>

              {bookingPreview && (
                <Card variant="outlined" sx={{ p: 2, boxShadow: 'none' }}>
                  {bookingPreview.allowed ? (
                    <Alert
                      severity={
                        bookingPreview.capacity_override_used
                          ? 'warning'
                          : 'success'
                      }
                    >
                      <Typography fontWeight={800}>
                        {bookingPreview.capacity_override_used
                          ? 'Beheeroverschrijding actief'
                          : 'Beschikbaar'}{' '}
                        · {money(bookingPreview.total_amount)}
                      </Typography>

                      {bookingPreview.capacity_override_used && (
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          {bookingPreview.reason}
                        </Typography>
                      )}

                      {bookingPreview.arrival_advice && (
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          {bookingPreview.arrival_advice}
                        </Typography>
                      )}

                      {!!bookingPreview.manual_dogs?.length && (
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          Handmatig beoordeeld: {bookingPreview.manual_dogs.join(', ')}
                        </Typography>
                      )}
                    </Alert>
                  ) : (
                    <Alert severity="warning">
                      {bookingPreview.reason}
                    </Alert>
                  )}
                </Card>
              )}

              {bookingPreview?.allowed &&
                Array.isArray(bookingPreview.price_lines) &&
                bookingPreview.price_lines.length > 0 && (
                  <Card
                    variant="outlined"
                    sx={{ p: 2, boxShadow: 'none' }}
                  >
                    <Typography fontWeight={800} sx={{ mb: 1 }}>
                      Prijsopbouw
                    </Typography>

                    <Stack spacing={0.75}>
                      {summarizePriceLines(bookingPreview.price_lines).map(
                        (line, index) => (
                          <Stack
                            key={`${line.dog_name}-${index}`}
                            direction={{ xs: 'column', sm: 'row' }}
                            justifyContent="space-between"
                            gap={0.5}
                          >
                            <Box>
                              <Typography variant="body2" fontWeight={700}>
                                {line.dog_name}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {line.count} × {money(line.final_rate)}
                                {line.discount_percent > 0
                                  ? ` · ${line.discount_percent}% gezinskorting`
                                  : ''}
                                {line.description
                                  ? ` · ${line.description}`
                                  : ''}
                              </Typography>
                            </Box>

                            <Typography fontWeight={800}>
                              {money(line.total)}
                            </Typography>
                          </Stack>
                        )
                      )}

                      <Divider />

                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        gap={2}
                      >
                        <Typography fontWeight={800}>
                          Totaal
                        </Typography>
                        <Typography fontWeight={800}>
                          {money(bookingPreview.total_amount)}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Card>
                )}

              {bookingPreview?.allowed && (
                <Card
                  variant="outlined"
                  sx={{ p: 2, boxShadow: 'none' }}
                >
                  <Typography fontWeight={800} sx={{ mb: 0.5 }}>
                    Moneybird-offerte
                  </Typography>

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={bookingQuoteRequired}
                        disabled={bookingBusy}
                        onChange={event => {
                          setBookingQuoteRequired(event.target.checked);
                          setBookingError('');
                        }}
                      />
                    }
                    label="De betaler moet nog een Moneybird-offerte ontvangen en online goedkeuren."
                  />

                  <Typography variant="body2" color="text.secondary">
                    Aangevinkt: de reservering staat meteen vast in de agenda,
                    maar de facturatie wacht op online goedkeuring van de offerte.
                    Niet aangevinkt: gebruik dit alleen als geen nieuwe offerte
                    meer nodig is.
                  </Typography>
                </Card>
              )}

              {bookingPreview?.allowed && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={bookingConfirmed}
                      onChange={event => {
                        setBookingConfirmed(event.target.checked);
                        setBookingError('');
                      }}
                    />
                  }
                  label="De opvangafspraak is definitief met de klant afgesproken."
                />
              )}

              {bookingPreview?.allowed &&
                bookingPreview.capacity_override_used && (
                  <Alert severity="warning">
                    <FormControlLabel
                      sx={{ m: 0 }}
                      control={
                        <Checkbox
                          checked={bookingCapacityConfirmed}
                          onChange={event => {
                            setBookingCapacityConfirmed(event.target.checked);
                            setBookingError('');
                          }}
                        />
                      }
                      label="Ik bevestig dat ik deze capaciteitsoverschrijding bewust accepteer."
                    />
                  </Alert>
                )}
            </Stack>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            disabled={bookingBusy}
            onClick={() => setBookingOpen(false)}
          >
            Sluiten
          </Button>

          <Button
            variant="contained"
            disabled={
              bookingBusy ||
              !bookingPreview?.allowed ||
              !bookingConfirmed ||
              (bookingPreview?.capacity_override_used && !bookingCapacityConfirmed) ||
              !selectedBookingFamily?.moneybird_contact_id
            }
            onClick={createAdminBooking}
          >
            {bookingBusy ? 'Opslaan...' : 'Definitieve reservering opslaan'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={blockOpen}
        onClose={() => {
          if (!blockBusy) setBlockOpen(false);
        }}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Datum of periode blokkeren</DialogTitle>

        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Gebruik dit bijvoorbeeld voor een nieuwe aanvraag die je nog moet
            beoordelen, maar waarvoor je de plek voorlopig wel vrij wilt houden.
            De blokkade geldt direct in de beschikbaarheidscontrole.
          </Alert>

          {blockError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {blockError}
            </Alert>
          )}

          {blockSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {blockSuccess}
            </Alert>
          )}

          <Stack spacing={2}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Vanaf"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: todayIso }}
                  value={blockStartDate}
                  onChange={event => {
                    const value = event.target.value;
                    setBlockStartDate(value);
                    if (!blockEndDate || blockEndDate < value) {
                      setBlockEndDate(value);
                    }
                    setBlockError('');
                    setBlockSuccess('');
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Tot en met"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: blockStartDate || todayIso }}
                  value={blockEndDate}
                  onChange={event => {
                    setBlockEndDate(event.target.value);
                    setBlockError('');
                    setBlockSuccess('');
                  }}
                />
              </Grid>
            </Grid>

            <TextField
              fullWidth
              label="Reden / notitie"
              placeholder="Bijv. voorlopige aanvraag nieuwe klant - 2 honden"
              value={blockReason}
              onChange={event => setBlockReason(event.target.value)}
              multiline
              minRows={2}
              helperText="Alleen bedoeld voor Pippaya-beheer."
            />

            <Box>
              <Typography fontWeight={800} sx={{ mb: 0.5 }}>
                Wat wil je blokkeren?
              </Typography>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={blockDaycare}
                    disabled={blockBusy}
                    onChange={event => {
                      setBlockDaycare(event.target.checked);
                      setBlockError('');
                    }}
                  />
                }
                label="Dagopvang"
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={blockOvernight}
                    disabled={blockBusy}
                    onChange={event => {
                      setBlockOvernight(event.target.checked);
                      setBlockError('');
                    }}
                  />
                }
                label="Logeren"
              />
            </Box>

            <Divider />

            <Box>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ sm: 'center' }}
                gap={1}
                sx={{ mb: 1.5 }}
              >
                <Box>
                  <Typography variant="h6">
                    Huidige en toekomstige blokkades
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Vrijgeven kan hier direct zodra een voorlopige aanvraag vervalt
                    of zodra je de echte reservering gaat invoeren.
                  </Typography>
                </Box>

                <Button
                  size="small"
                  startIcon={<RefreshRoundedIcon />}
                  disabled={blockLoading || blockBusy}
                  onClick={loadFutureBlocks}
                >
                  Vernieuwen
                </Button>
              </Stack>

              {blockLoading ? (
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 2 }}>
                  <CircularProgress size={22} />
                  <Typography color="text.secondary">
                    Blokkades laden...
                  </Typography>
                </Stack>
              ) : blockRows.length ? (
                <Stack spacing={1}>
                  {blockRows.map(block => (
                    <Paper
                      key={block.id}
                      variant="outlined"
                      sx={{ p: 1.5, boxShadow: 'none' }}
                    >
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        justifyContent="space-between"
                        alignItems={{ sm: 'center' }}
                        gap={1.5}
                      >
                        <Box>
                          <Typography fontWeight={800}>
                            {block.start_date === block.end_date
                              ? nlLong.format(parseDate(block.start_date))
                              : `${nlLong.format(parseDate(block.start_date))} t/m ${nlLong.format(parseDate(block.end_date))}`}
                          </Typography>

                          <Stack
                            direction="row"
                            spacing={0.75}
                            flexWrap="wrap"
                            useFlexGap
                            sx={{ mt: 0.75 }}
                          >
                            {block.blocks_daycare && (
                              <Chip size="small" label="Dagopvang geblokkeerd" />
                            )}
                            {block.blocks_overnight && (
                              <Chip size="small" label="Logeren geblokkeerd" />
                            )}
                          </Stack>

                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                            {block.reason || 'Geen reden ingevuld'}
                          </Typography>
                        </Box>

                        <Button
                          variant="outlined"
                          disabled={blockBusy}
                          onClick={() => releaseBlock(block)}
                        >
                          Vrijgeven
                        </Button>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              ) : (
                <Typography color="text.secondary">
                  Geen huidige of toekomstige handmatige blokkades.
                </Typography>
              )}
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button
            disabled={blockBusy}
            onClick={() => setBlockOpen(false)}
          >
            Sluiten
          </Button>

          <Button
            variant="contained"
            disabled={
              blockBusy ||
              !blockStartDate ||
              !blockEndDate ||
              (!blockDaycare && !blockOvernight)
            }
            onClick={saveBlock}
          >
            {blockBusy ? 'Opslaan...' : 'Blokkering opslaan'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
